import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, getFns } from '../../firebase'
import Page from '../../components/layout/Page'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { TimesheetPresenceReasons, timesheetTime } from '../../components/TimesheetClockCard'
import PhotoReviewPanel from './PhotoReviewPanel'
import ReviewDashboardPanel from './ReviewDashboardPanel'

export function TimesheetReviewPanel() {
  const [pin, setPin] = useState('')
  const [signedIn, setSignedIn] = useState(false)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const credentialRef = useRef(null)
  const requestRef = useRef(0)
  const busyRef = useRef(false)

  const clearSession = () => {
    ++requestRef.current; busyRef.current = false; credentialRef.current = null
    setPin(''); setResult(null); setBusy(''); setError('')
  }
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => { clearSession(); setSignedIn(Boolean(user)) })
    const hidden = () => { if (document.visibilityState !== 'visible') clearSession() }
    document.addEventListener('visibilitychange', hidden)
    return () => { unsubscribe(); document.removeEventListener('visibilitychange', hidden); credentialRef.current = null; ++requestRef.current }
  }, [])

  const load = async (older = false) => {
    if (busyRef.current || !auth.currentUser) return
    if (older && (!result?.nextCursor || !credentialRef.current)) return
    if (pin && !/^\d{4,8}$/.test(pin)) { setError('Enter a 4–8 digit reviewer PIN.'); return }
    busyRef.current = true
    const request = ++requestRef.current
    const credentials = older ? credentialRef.current : pin ? { pin } : {}
    setBusy(older ? 'older' : 'load'); setError('')
    if (!older) { setResult(null); credentialRef.current = null }
    try {
      const { data } = await httpsCallable(getFns(), 'timesheetReviewerList')({ ...credentials, ...(older ? { cursor: result.nextCursor } : {}) })
      if (request !== requestRef.current) return
      if (!Array.isArray(data?.notifications) || !data.reviewer) throw new Error('Invalid alerts response')
      setResult((current) => older && current ? {
        ...data, notifications: [...current.notifications, ...data.notifications.filter((item) => !current.notifications.some((existing) => existing.id === item.id))],
      } : data)
      credentialRef.current = credentials
    } catch {
      if (request === requestRef.current) setError('Could not open timesheet alerts. Check your reviewer PIN or ask an administrator.')
    } finally {
      if (request === requestRef.current) { busyRef.current = false; setBusy('') }
    }
  }

  const acknowledge = async (notificationId) => {
    if (busyRef.current || !credentialRef.current) return
    busyRef.current = true
    const request = ++requestRef.current
    setBusy(notificationId); setError('')
    try {
      const { data } = await httpsCallable(getFns(), 'timesheetReviewerAcknowledge')({ ...credentialRef.current, notificationId })
      if (request !== requestRef.current) return
      if (data?.ok !== true || !data.readAt) throw new Error('Invalid acknowledgement')
      setResult((current) => ({ ...current, notifications: current.notifications.map((item) => item.id === notificationId ? { ...item, readAt: data.readAt } : item) }))
    } catch {
      if (request === requestRef.current) setError('Could not mark this alert as read. Please try again.')
    } finally {
      if (request === requestRef.current) { busyRef.current = false; setBusy('') }
    }
  }

  return <div className="space-y-5">
    <Card>
      <h2 className="text-lg font-semibold text-foreground">Open timesheet alerts</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Cinara signs in with her own account and enters her reviewer PIN. Administrators may leave the PIN blank.</p>
      <div className="mt-4 max-w-sm space-y-3">
        <Input id="timesheet-reviewer-pin" label="Reviewer PIN" type="password" inputMode="numeric" autoComplete="off" maxLength={8} value={pin} onChange={(event) => { setPin(event.target.value.replace(/\D/g, '').slice(0, 8)); setResult(null); credentialRef.current = null }} disabled={Boolean(busy) || !signedIn} />
        <div className="flex flex-wrap gap-2">
          <Button type="button" iconName="Bell" loading={busy === 'load'} disabled={Boolean(busy) || !signedIn} onClick={() => load()}>Load alerts</Button>
          {result && <Button type="button" variant="outline" disabled={Boolean(busy)} onClick={clearSession}>Close alerts</Button>}
        </div>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Alerts are available inside this app. The PIN and alert list are cleared when you leave the app or close this view.</p>
    </Card>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {result && <div className="space-y-4">
      <div role="status">
        <h2 className="text-lg font-semibold text-foreground">Alerts for {result.reviewer.name}</h2>
        <p className="mt-1 text-sm text-muted-foreground">Showing {result.notifications.length} {result.notifications.length === 1 ? 'alert' : 'alerts'}, newest first. Each page contains up to 50. {result.hasMore ? 'Load older alerts to continue.' : ''}</p>
      </div>
      {result.notifications.length === 0 && <Card><p className="text-sm text-muted-foreground">No timesheet alerts to show.</p></Card>}
      {result.notifications.map((item) => <Card key={item.id} aria-label={`Clock-in alert for ${item.operatorName}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-foreground">{item.operatorName}</h3>
            <p className="mt-1 text-xs text-muted-foreground">Clock-in alert · {timesheetTime(item.createdAt)}</p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${item.readAt ? 'bg-muted text-muted-foreground' : 'bg-warning/10 text-warning'}`}>{item.readAt ? 'Read' : 'Unread'}</span>
        </div>
        <div className="mt-4"><TimesheetPresenceReasons presence={{ network: { status: item.networkStatus }, location: { status: item.locationStatus } }} /></div>
        {item.employeeConfirmedAtWork === true && <p className="mt-3 text-sm text-foreground">The employee confirmed they were at the workplace.</p>}
        {(Number.isFinite(item.distanceMeters) || Number.isFinite(item.accuracyMeters)) && <p className="mt-2 text-xs text-muted-foreground">{Number.isFinite(item.distanceMeters) ? `Distance from store center: ${Math.round(item.distanceMeters)} m. ` : ''}{Number.isFinite(item.accuracyMeters) ? `Reported accuracy: ±${Math.round(item.accuracyMeters)} m.` : ''}</p>}
        {item.readAt ? <p className="mt-4 text-xs text-muted-foreground">Read at {timesheetTime(item.readAt)}</p> : <Button type="button" size="sm" variant="outline" className="mt-4" loading={busy === item.id} disabled={Boolean(busy)} onClick={() => acknowledge(item.id)}>Mark as read</Button>}
      </Card>)}
      {result.hasMore && result.nextCursor && <Button type="button" variant="outline" loading={busy === 'older'} disabled={Boolean(busy)} onClick={() => load(true)}>Load older alerts</Button>}
    </div>}
  </div>
}

export default function TimesheetReviewPage() {
  return <Page size="full" className="review-page max-w-[1560px]"><ReviewDashboardPanel /></Page>
}
